import { Trans, useTranslation } from "react-i18next";
import defaultContent from "../../../content/earningsCalculator";
import "./EarningsCalculatorInfoSlider.scss";
import LeafPanel, { ModalProps } from "../../../common/leaf/LeafPanel/LeafPanel";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { Settings } from "../../../services/EarningsCalculator/earningCalculatorAPI.types";
import { useAppSelector } from "../../../store";

type Props = ModalProps & {
  IsLoading: boolean;
};

const EarningsCalculatorInfoSlider = ({ show, IsLoading, onCloseClick, title, id }: Props) => {
  const { t } = useTranslation(["earningsCalculator"]);
  const { ECComputedData } = useAppSelector((state: any) => state.earningCalculator);
  const { Settings } = ECComputedData;

  return (
    <div className="EarningsCalculatorInfoSlider">
      <LeafPanel {...{ show, onCloseClick, title, id }} applyScroll={true}>
        <LeafRectangleLoader height="140px" width="500px" isLoading={IsLoading} numRows={7}>
          <section className="EarningsCalculatorInfoSlider__wrapper">
            <section>
              <h2 className="EarningsCalculatorInfoSlider__title">
                <Trans>
                  {t("infoSlider.totalEarningTitle", defaultContent.infoSlider.totalEarningTitle).toString()}
                </Trans>
              </h2>
              <p className="EarningsCalculatorInfoSlider__desc">
                <Trans>{t("infoSlider.totalEarningDesc", defaultContent.infoSlider.totalEarningDesc).toString()}</Trans>
              </p>
            </section>
            <section>
              <h2 className="EarningsCalculatorInfoSlider__title">
                <Trans>{t("infoSlider.groupPVTitle", defaultContent.infoSlider.groupPVTitle).toString()}</Trans>
              </h2>
              <p className="EarningsCalculatorInfoSlider__desc">
                <Trans>{t("infoSlider.groupPVDesc", defaultContent.infoSlider.groupPVDesc).toString()}</Trans>
              </p>
            </section>
            <section hidden={!Settings.CSI.Active}>
              <h2 className="EarningsCalculatorInfoSlider__title">
                <Trans>{t("infoSlider.vcsPVTitle", defaultContent.infoSlider.vcsPVTitle).toString()}</Trans>
              </h2>
              <p className="EarningsCalculatorInfoSlider__desc">
                <Trans>{t("infoSlider.vcsPVDesc", defaultContent.infoSlider.vcsPVDesc).toString()}</Trans>
              </p>
            </section>
            <section>
              <h2 className="EarningsCalculatorInfoSlider__title">
                <Trans>
                  {t(
                    "infoSlider.performanceBonusTypesTitle",
                    defaultContent.infoSlider.performanceBonusTypesTitle,
                  ).toString()}
                </Trans>
              </h2>
              <p className="EarningsCalculatorInfoSlider__desc">
                <span className="EarningsCalculatorInfoSlider__desc-subtitle">
                  <Trans>
                    {t(
                      "infoSlider.personalPerformanceBonusSubTitle",
                      defaultContent.infoSlider.personalPerformanceBonusSubTitle,
                    ).toString()}
                  </Trans>
                </span>
                :{" "}
                <Trans>
                  {t(
                    "infoSlider.personalPerformanceBonusDesc",
                    defaultContent.infoSlider.personalPerformanceBonusDesc,
                  ).toString()}
                </Trans>
              </p>
              <p className="EarningsCalculatorInfoSlider__desc">
                <span className="EarningsCalculatorInfoSlider__desc-subtitle">
                  <Trans>
                    {t(
                      "infoSlider.differentialPerformanceBonusSubTitle",
                      defaultContent.infoSlider.differentialPerformanceBonusSubTitle,
                    ).toString()}
                  </Trans>
                </span>
                :{" "}
                <Trans>
                  {t(
                    "infoSlider.differentialPerformanceBonusDesc",
                    defaultContent.infoSlider.differentialPerformanceBonusDesc,
                  ).toString()}
                </Trans>
              </p>
            </section>
            <section hidden={!Settings.CSI.Active}>
              <h2 className="EarningsCalculatorInfoSlider__title">
                <Trans>
                  {t(
                    "infoSlider.customerSalesIncentiveTitle",
                    defaultContent.infoSlider.customerSalesIncentiveTitle,
                  ).toString()}
                </Trans>
              </h2>
              <p className="EarningsCalculatorInfoSlider__desc">
                <Trans>
                  {t("infoSlider.customerSalesIncentiveDesc", defaultContent.infoSlider.customerSalesIncentiveDesc, {
                    MaxCSIPercent: Settings.CSI.MaxCSIPercent.Formatted,
                  })}
                </Trans>
              </p>
            </section>
            <section hidden={!Settings.BronzeFoundation.Active}>
              <h2 className="EarningsCalculatorInfoSlider__title">
                <Trans>
                  {t("infoSlider.bronzeFoundationTitle", defaultContent.infoSlider.bronzeFoundationTitle).toString()}
                </Trans>
              </h2>
              <p className="EarningsCalculatorInfoSlider__desc">
                <Trans>
                  {t("infoSlider.bronzeFoundationDesc", defaultContent.infoSlider.bronzeFoundationDesc, {
                    PerformanceBonusTarget: Settings.BronzeFoundation.Requirements.PerformanceBonus.Target,
                    LegsPercentageTarget: Settings.BronzeFoundation.Requirements.LegsPercentage.Target,
                    LegsPercentagePercent: Settings.BronzeFoundation.Requirements.LegsPercentage.Percent,
                  })}
                </Trans>
              </p>
              <p
                className="EarningsCalculatorInfoSlider__desc"
                hidden={!Settings.BronzeFoundation.Requirements.VCSBonusLegs.Required}
              >
                <Trans>
                  {t(
                    "infoSlider.bronzeFoundationRequiredNote",
                    defaultContent.infoSlider.bronzeFoundationRequiredNote,
                    {
                      VCSBonusLegsTarget: Settings.BronzeFoundation.Requirements.VCSBonusLegs.Target,
                      VCSBonusLegsTargetVCSPV: Settings.BronzeFoundation.Requirements.VCSBonusLegs.TargetVCSPV,
                    },
                  )}
                </Trans>
              </p>
            </section>
            <section hidden={!Settings.BronzeFoundation.ConsistencyPayment.Active}>
              <h2 className="EarningsCalculatorInfoSlider__title">
                <Trans>
                  {t(
                    "infoSlider.bronzeFoundationConsistencyTitle",
                    defaultContent.infoSlider.bronzeFoundationConsistencyTitle,
                  ).toString()}
                </Trans>
              </h2>
              <p className="EarningsCalculatorInfoSlider__desc">
                <Trans>
                  {t(
                    "infoSlider.bronzeFoundationConsistencyDesc",
                    defaultContent.infoSlider.bronzeFoundationConsistencyDesc,
                  ).toString()}
                </Trans>
              </p>
            </section>
            <section hidden={!Settings.BronzeBuilder.Active}>
              <h2 className="EarningsCalculatorInfoSlider__title">
                <Trans>
                  {t("infoSlider.bronzeBuilderTitle", defaultContent.infoSlider.bronzeBuilderTitle).toString()}
                </Trans>
              </h2>
              <p className="EarningsCalculatorInfoSlider__desc">
                <Trans>
                  {t("infoSlider.bronzeBuilderDesc", defaultContent.infoSlider.bronzeBuilderDesc, {
                    PerformanceBonusTarget: Settings.BronzeBuilder.Requirements.PerformanceBonus.Target,
                    LegsPercentageTarget: Settings.BronzeBuilder.Requirements.LegsPercentage.Target,
                    LegsPercentagePercent: Settings.BronzeBuilder.Requirements.LegsPercentage.Percent,
                  })}
                </Trans>
              </p>
              <p
                className="EarningsCalculatorInfoSlider__desc"
                hidden={!Settings.BronzeBuilder.Requirements.VCSBonusLegs.Required}
              >
                <Trans>
                  {t(
                    "infoSlider.bronzeFoundationRequiredNote",
                    defaultContent.infoSlider.bronzeFoundationRequiredNote,
                    {
                      VCSBonusLegsTarget: Settings.BronzeBuilder.Requirements.VCSBonusLegs.Target,
                      VCSBonusLegsTargetVCSPV: Settings.BronzeBuilder.Requirements.VCSBonusLegs.TargetVCSPV,
                    },
                  )}
                </Trans>
              </p>
            </section>
            <section hidden={!Settings.BronzeBuilder.ConsistencyPayment.Active}>
              <h2 className="EarningsCalculatorInfoSlider__title">
                <Trans>
                  {t(
                    "infoSlider.bronzeBuilderConsistencyTitle",
                    defaultContent.infoSlider.bronzeBuilderConsistencyTitle,
                  ).toString()}
                </Trans>
              </h2>
              <p className="EarningsCalculatorInfoSlider__desc">
                <Trans>
                  {t(
                    "infoSlider.bronzeBuilderConsistencyDesc",
                    defaultContent.infoSlider.bronzeBuilderConsistencyDesc,
                  ).toString()}
                </Trans>
              </p>
            </section>
          </section>
        </LeafRectangleLoader>
      </LeafPanel>
    </div>
  );
};

export default EarningsCalculatorInfoSlider;
